import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Form, Input, Checkbox, Spin, Radio, Select } from 'antd';
import LoadableButton from "../../../components/buttons/LoadableButton.jsx";
import { GetAccountsApi, GetWalletApi, InternalTransferApi } from '../../../api/request/transfer.js';
import TransferRequest from './TransferRequest.jsx';
import { WalletOutlined, PlusCircleOutlined, DownOutlined, BankOutlined } from '@ant-design/icons';
import BalanceCard from '../dashboard/components/BalanceCardSM.jsx';

const TransferMobile = () => {
  const { userDetails } = useSelector(state => state.user);
  const [form] = Form.useForm();
  const [wallet, setWallet] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fromAccount, setFromAccount] = useState(null);
  const [toAccount, setToAccount] = useState(null);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  
  const getWallet = async () => {
    setIsLoading(true);
    try {
      const params = { RuleType: 'InternalTransfer' };
      const { data } = await GetWalletApi(userDetails?.id, params);
      if (data?.success) setWallet(data?.data)
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }

  const getAccounts = async () => {
    setIsLoading(true);
    try {
      const { data } = await GetAccountsApi(true);
      if (data?.success) {
        setAccounts([
          { terminalID: "wallet" },
          ...data?.data?.filter((account) => account?.isLive && account?.terminalID !== 0),
        ]);
      }
    } catch (error) {
      if (error?.response?.data?.error?.message)
        toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getWallet();
    getAccounts();
  }, []);

  const resetAll = () => {
    form.resetFields();
    setFromAccount(null);
    setToAccount(null);
  }

  const handleSubmit = async (values) => {
    setIsSubmitLoading(true);
    try {
      const isWalletToAccount = values?.fromAccount === 'wallet';
      const isAccountToWallet = values?.toAccount === 'wallet';
      let data = null;

      if (isWalletToAccount || isAccountToWallet) {
        const res = await InternalTransferApi({
          amount: Number(values?.amount),
          sourceTerminalId: isAccountToWallet ? values?.fromAccount : 0,
          destinationTerminalId: isWalletToAccount ? values?.toAccount : 0,
          transferType: isWalletToAccount ? 1 : 2,
        });
        data = res?.data;
      } else {
        const res = await InternalTransferApi({
          amount: Number(values?.amount),
          sourceTerminalId: values?.fromAccount,
          destinationTerminalId: values?.toAccount,
          transferType: 3,
        });
        data = res?.data;
      }

      if (data?.success) {
        toast.success(data?.message);
        resetAll();
        getWallet();
        getAccounts();
        setIsRefresh(true);
      }
    } catch (error) {
      if (error?.response?.data?.error?.message)
        toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsSubmitLoading(false);
    }
  }

  const validateAmount = (_, value) => {
    if (!value) return Promise.reject(new Error('Please enter amount.'));

    if (Number(value) <= 0)
      return Promise.reject(new Error('Please enter a valid amount.'));

    if (wallet?.isRuleActive) {
      if (value < wallet?.minAmount) {
        return Promise.reject(new Error(`Amount must be at least $${wallet?.minAmount}.`));
      }
      if (value > wallet?.maxAmount) {
        return Promise.reject(new Error(`Amount cannot exceed $${wallet?.maxAmount}.`));
      }
    }

    return Promise.resolve();
  };

  const validateAccount = (_, value) => {
    if (!value) return Promise.resolve();

    if (fromAccount === toAccount) {
      return Promise.reject(new Error('From and To accounts must be different.'));
    }

    return Promise.resolve();
  };

  const renderAccounts = (type, value) => (
    <Select
      value={type === 'from' ? fromAccount : toAccount}
      onChange={type === 'from' ? setFromAccount : setToAccount}
      className="w-full !bg-white rounded-xl"
      size="large"
      suffixIcon={<div className="flex items-center justify-center h-full w-full"><DownOutlined className="text-gray-400 bg-[#F5F9FD] p-2.5 rounded-lg" /></div>}
      placeholder={`Select ${type === 'from' ? 'From' : 'To'} Account`}
      popupClassName="custom-select-dropdown"
      dropdownAlign={{ offset: [0, 0] }}
      dropdownStyle={{
        borderRadius: '16px',
        padding: '8px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
        border: 'none',
        marginTop: '-10px'
      }}
    >
      {accounts.map(item => (
        <Select.Option
          key={item?.terminalID}
          value={item?.terminalID}
          label={<span className="primary font-semibold text-sm">{item?.name}</span>}
        >
          <div className="flex items-center gap-2 py-1 px-2 rounded-lg">
            {item?.terminalID === "wallet" ? <WalletOutlined size={24} /> : <BankOutlined size={24} />}
            <span className={`font-semibold text-sm`}>
              {item?.terminalID} {item?.terminalID !== "wallet" ? `($${item?.balance || 0})` : ''}
            </span>
          </div>
        </Select.Option>
      ))}
    </Select>
  )

  const balanceCardsData = [
    { id: 1, type: 'Wallet No.', amount: wallet?.walletAccountNo || 0.00, color: 'bg-[#9810FA]', gradient: 'from-[#FAF5FF] to-[#ffff]', icon: <WalletOutlined size={20} color='#fff' className='text-white' /> },
    { id: 2, type: 'Wallet Balance', amount: wallet?.walletAmount || 0.00, color: 'bg-green-500', gradient: 'from-[#F0FDF4] to-[#ffff]', icon: <PlusCircleOutlined size={20} color='#fff' className='text-white' /> },
    { id: 3, type: 'Min Deposit', amount: wallet?.minAmount || 0.00, color: 'bg-orange-500', gradient: 'from-[#FFF7ED] to-[#ffff]', icon: <WalletOutlined size={20} color='#fff' className='text-white' /> },
    { id: 4, type: 'Max Deposit', amount: wallet?.maxAmount || 0.00, color: 'bg-red-500', gradient: 'from-[#FFEDED] to-[#ffff]', icon: <WalletOutlined size={20} color='#fff' className='text-white' /> }
  ];

  return (
    <div className='mobile-dashboard min-h-screen'>
      <div className="grid grid-cols-4 gap-3 mb-6">
        {balanceCardsData.map((card, index) => (
          <BalanceCard
            key={card.id || index}
            type={card.type}
            amount={card.amount}
            color={card.color}
            gradient={card.gradient}
            icon={card.icon}
            className="text-center px-2 pb-1 py-2"
            innerClassName="flex-col gap-2 !mb-0"
            titleClass='!text-[8px] !leading-none !ml-0'
            amountClass='!text-[10px]'
          />
        ))}
      </div>

      <div className="mb-4">
        <Form form={form} layout='vertical' className='space-y-5' onFinish={handleSubmit}>
          <Form.Item
            name='amount'
            label={<span className="text-lg text-[#314158] font-medium">Amount In USD</span>}
            rules={[{ validator: validateAmount }]}
            className='w-full'
          >
            <Input type='number' placeholder="Amount in USD" size='large' className='py-3' />
          </Form.Item>
          <Form.Item
            name="fromAccount"
            label={<span className="text-lg text-[#314158] font-medium">Select From Account</span>}
            rules={[{ required: true, message: 'Please select an account.' }]}
          >
            {renderAccounts('from', fromAccount)}
          </Form.Item>
          <Form.Item
            name="toAccount"
            dependencies={['fromAccount']}
            label={<span className="text-lg text-[#314158] font-medium">Select To Account</span>}
            rules={[{ required: true, message: 'Please select an account.' }, { validator: validateAccount }]}
          >
            {renderAccounts('to', toAccount)}
          </Form.Item>

          <Form.Item
            name='conditions'
            valuePropName="checked"
            rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms and conditions.')) }]}
          >
            <Checkbox className='mt-4'>
              Yes, I agreed to the <Link to={'/terms_&_conditions'} className='primary hover:underline duration-300'>Terms and conditions</Link>
            </Checkbox>
          </Form.Item>

          <LoadableButton
            className='bg-primary-mobile text-sm text-white font-semibold rounded-lg px-12 py-4 w-full uppercase themeHover duration-500'
            type="submit"
            lable='Submit'
            isLoading={isSubmitLoading}
            loadingLable='Processing...'
          />
        </Form>
      </div>

      <TransferRequest isRefresh={isRefresh} setIsRefresh={setIsRefresh} />
    </div>
  )
}

export default TransferMobile
